import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { Slot } from "radix-ui"

const cardVariants = cva(
  "group/card flex flex-col rounded-lg border border-border bg-card text-card-foreground",
  {
    variants: {
      variant: {
        flat: "",
        raised: "shadow-raised",
      },
      size: {
        sm: "gap-3 py-4",
        md: "gap-4 py-5",
        lg: "gap-5 py-6",
      },
    },
    defaultVariants: {
      variant: "flat",
      size: "md",
    },
  },
)

function Card({
  className,
  variant = "flat",
  size = "md",
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof cardVariants>) {
  return (
    <div
      data-slot="card"
      data-size={size}
      data-variant={variant}
      className={cn(cardVariants({ variant, size }), className)}
      {...props}
    />
  )
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1 px-5 group-data-[size=lg]/card:px-6 group-data-[size=sm]/card:px-4 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-4",
        className,
      )}
      {...props}
    />
  )
}

/**
 * An h3 by default, so the page outline lists it. Use `asChild` to render the
 * level the page needs, for example `<CardTitle asChild><h2>…</h2></CardTitle>`.
 */
function CardTitle({
  asChild = false,
  className,
  ...props
}: React.ComponentProps<"h3"> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "h3"
  return (
    <Comp
      data-slot="card-title"
      className={cn(
        "font-display text-h3 group-data-[size=sm]/card:text-body group-data-[size=sm]/card:font-medium",
        className,
      )}
      {...props}
    />
  )
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-body-sm text-muted-foreground", className)}
      {...props}
    />
  )
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className,
      )}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn(
        "px-5 text-ui group-data-[size=lg]/card:px-6 group-data-[size=sm]/card:px-4",
        className,
      )}
      {...props}
    />
  )
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn(
        "flex items-center gap-2 px-5 group-data-[size=lg]/card:px-6 group-data-[size=sm]/card:px-4 [.border-t]:pt-4",
        className,
      )}
      {...props}
    />
  )
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
  cardVariants,
}
